import React from 'react';
import { useFormContext } from 'react-hook-form';
import _ from 'lodash';
import { Box, Grid as MuiGrid, InputAdornment } from '@mui/material';
import { InputLabelBase } from '../../inputs/InputLabelBase';
import { SimpleSelect } from '../../inputs/selects/SimpleSelect';
import { TextFieldBase } from '../../inputs/TextFieldBase';
import { PhoneField as PhoneFieldBase } from '../../inputs/PhoneField';
import { DomainField as DomainFieldBase } from '../../inputs/DomainField';
import { NaicsCodeField } from '../../inputs/NaicsCodeField';
import { withFormController } from '../../hocs/forms';
import { withShowable } from '../../../console/_global/lib/withShowable';
// utils
import { yesNoOptions } from '../../../utils';
import { UsStates } from '../../../utils/USState';
import LanguageCurrencyFieldBase from '../../inputs/LanguageCurrencyField';

// components

const autoCompleteLabelProps = {
  style: { paddingTop: 0 },
  indent: false,
};

export const inputProps = {
  style: {
    border: 'none',
  },
};

const entityTypeOptions = [
  { value: 'Independent', label: 'Independent' },
  { value: 'Holding', label: 'Holding' },
  { value: 'Subsidiary', label: 'Subsidiary' },
  { value: 'Branch', label: 'Branch' },
  { value: 'Parent', label: 'Parent' },
];

const ownershipTypeOptions = [
  { value: 'Public', label: 'Public' },
  { value: 'Private', label: 'Private' },
  { value: 'Non-Profit', label: 'Non-Profit' },
  { value: 'Public Sector', label: 'Public Sector' },
  { value: 'Partnership', label: 'Partnership' },
  { value: 'Non-Corporates', label: 'Non-Corporates' },
];

const LanguageCurrencyField = withFormController(LanguageCurrencyFieldBase);
const Select = withFormController(SimpleSelect);
const PhoneField = withFormController(PhoneFieldBase);
const TextField = withFormController(TextFieldBase);

const Grid = withShowable(MuiGrid);

function useAccountDetailsRenewalsField(
  showableQuestions: any,
  accountData: any
) {
  return React.useMemo(() => {
    const SHOW_SECONDARY_NAICS =
      showableQuestions.includes('secondaryNaics') &&
      _.get(accountData, 'primaryNaicsPercentage') !== 100;

    const SHOW_NAICS_SECONDARY_PERCENT =
      showableQuestions.includes('secondaryNaicsPercentage') &&
      _.get(accountData, 'primaryNaicsPercentage') !== 100;

    const SHOW_ALL_NAICS =
      SHOW_SECONDARY_NAICS && !SHOW_NAICS_SECONDARY_PERCENT;

    return {
      SHOW_COMPANY_NAME: showableQuestions.includes('name'),
      SHOW_DBA: showableQuestions.includes('dbaOrTradestyle'),
      SHOW_DUNS_NUMBER: showableQuestions.includes('dunsNumber'),
      SHOW_NO_EMPLOYEES: showableQuestions.includes('noOfEmployeesAll'),
      SHOW_IS_FRANCHISE: showableQuestions.includes('isFranchise'),
      SHOW_ENTITY_TYPE: showableQuestions.includes('entityType'),
      SHOW_YEAR_ESTABLISHED: showableQuestions.includes('yearEstablished'),
      SHOW_OWNERSHIP_TYPE: showableQuestions.includes('ownershipType'),
      SHOW_DOMAIN_NAME: showableQuestions.includes('domainName'),
      SHOW_DOMAINS: showableQuestions.includes('domains'),
      SHOW_URL: showableQuestions.includes('url'),
      SHOW_NO_PERSONAL_RECORDS: showableQuestions.includes(
        'numberOfPersonalRecords'
      ),
      SHOW_INT_SALES: showableQuestions.includes('percentInternationalSales'),
      SHOW_PHONE_NUMBER: showableQuestions.includes('phoneNumber'),
      SHOW_NAICS: showableQuestions.includes('naicsCode'),
      SHOW_NAICS_PRIMARY_PERCENT: showableQuestions.includes(
        'primaryNaicsPercentage'
      ),
      SHOW_ADDRESS: showableQuestions.includes('address1'),
      SHOW_CITY: showableQuestions.includes('city'),
      SHOW_STATE: showableQuestions.includes('state'),
      SHOW_ZIP_CODE: showableQuestions.includes('zipCode'),
      SHOW_SECONDARY_NAICS,
      SHOW_NAICS_SECONDARY_PERCENT,
      SHOW_ALL_NAICS,
    };
  }, [accountData, showableQuestions]);
}

// need to introduce accountData here for hook logic
export const AccountDetailsRenewalFields = ({
  disabled,
  showableQuestions,
  accountData,
}: any) => {
  const methods = useFormContext();
  const { primaryNaicsPercentage, ...values } = methods.getValues();
  const {
    SHOW_COMPANY_NAME,
    SHOW_DBA,
    SHOW_DUNS_NUMBER,
    SHOW_IS_FRANCHISE,
    SHOW_ENTITY_TYPE,
    SHOW_YEAR_ESTABLISHED,
    SHOW_OWNERSHIP_TYPE,
    SHOW_DOMAIN_NAME,
    SHOW_DOMAINS,
    SHOW_URL,
    SHOW_NO_PERSONAL_RECORDS,
    SHOW_INT_SALES,
    SHOW_PHONE_NUMBER,
    SHOW_NAICS,
    SHOW_NAICS_PRIMARY_PERCENT,
    // @ts-expect-error TS(2339): Property 'SHOW_NAICS_SECONDARY_NAICS' does not exi... Remove this comment to see the full error message
    SHOW_NAICS_SECONDARY_NAICS,
    SHOW_NAICS_SECONDARY_PERCENT,
    SHOW_ALL_NAICS,
    SHOW_ADDRESS,
    SHOW_CITY,
    SHOW_STATE,
    SHOW_ZIP_CODE,
  } = useAccountDetailsRenewalsField(showableQuestions, accountData);

  const handlePrimaryNaics = (naicsData: any) => {
    methods.setValue('naicsCode', naicsData.value);
    methods.setValue('primaryIndustry', naicsData?.meta?.primaryIndustry);
    methods.setValue('secondaryIndustry', naicsData?.meta?.secondaryIndustry);
  };

  const handleSecondaryNaics = (naicsData: any) => {
    methods.setValue('secondNaicsCode', naicsData?.value ?? undefined);
    methods.setValue(
      'secondPrimaryIndustry',
      naicsData?.meta?.primaryIndustry ?? undefined
    );
    methods.setValue(
      'secondSecondaryIndustry',
      naicsData?.meta?.secondaryIndustry ?? undefined
    );
  };

  return (
    <Grid
        container
        spacing={2}
        direction="row"
        style={{ padding: '0 2.08rem' }}
      >
        <Grid show={SHOW_COMPANY_NAME} item md={6}>
          <InputLabelBase required>Name</InputLabelBase>
          <TextField
            name="name"
            inputProps={inputProps}
            fullWidth
            placeholder="Company name"
            disabled={disabled}
          />
        </Grid>
        <Grid item md={6} show={SHOW_DBA}>
          <InputLabelBase>DBA</InputLabelBase>
          <TextField
            name="dbaOrTradestyle"
            inputProps={inputProps}
            fullWidth
            placeholder="DBA"
            disabled={disabled}
          />
        </Grid>
        <Grid item md={6} show={SHOW_NAICS || SHOW_ALL_NAICS}>
          <InputLabelBase className="classes.inputLabelCustom" required>
            Primary Industry Code
          </InputLabelBase>
          <NaicsCodeField
            name="naicsCode"
            labelProps={autoCompleteLabelProps}
            placeholder="Primary Industry code"
            defaultValue={values.naicsCode}
            required
            textFieldProps={inputProps}
            disabled={disabled}
            onChange={handlePrimaryNaics}
          />
        </Grid>
        <Grid item md={6} show={SHOW_NAICS_PRIMARY_PERCENT || SHOW_ALL_NAICS}>
          <TextField
            name="primaryNaicsPercentage"
            defaultValue={primaryNaicsPercentage}
            inputProps={{
              min: 51,
              max: 100,
              ...inputProps,
            }}
            type="number"
            placeholder="75"
            disabled={disabled}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
          />
        </Grid>
        <Grid item md={6} show={SHOW_NAICS_SECONDARY_NAICS || SHOW_ALL_NAICS}>
          <InputLabelBase
            className="classes.inputLabelCustom"
            required={primaryNaicsPercentage != 100}
          >
            Secondary Industry Code
          </InputLabelBase>
          <NaicsCodeField
            name="secondNaicsCode"
            required
            labelProps={autoCompleteLabelProps}
            placeholder="Secondary Industry code"
            defaultValue={values.secondNaicsCode}
            readOnly={false}
            textFieldProps={inputProps}
            disabled={disabled}
            disableClearable={false}
            onChange={handleSecondaryNaics}
          />
        </Grid>
        <Grid item md={6} show={SHOW_NAICS_SECONDARY_PERCENT || SHOW_ALL_NAICS}>
          <TextField
            name="secondaryNaicsPercentage"
            defaultValue={100 - Number(primaryNaicsPercentage)}
            inputProps={{
              min: 0,
              max: 49,
              ...inputProps,
            }}
            type="number"
            placeholder="25"
            disabled
            fullWidth
            value={100 - Number(primaryNaicsPercentage)}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
          />
        </Grid>
        <Grid item md={6}>
          <InputLabelBase className="classes.inputLabelCustom" required>
            Projected Revenue
          </InputLabelBase>
          <Box>
            <LanguageCurrencyField
              name="revenue"
              defaultValue={values.revenue}
              inputProps={inputProps}
              fullWidth
              disabled={disabled}
            />
          </Box>
        </Grid>
        <Grid item md={6} show={SHOW_DUNS_NUMBER}>
          <InputLabelBase className="classes.inputLabelCustom">
            DUNS
          </InputLabelBase>
          <Box>
            <TextField
              name="dunsNumber"
              defaultValue={values.dunsNumber}
              inputProps={inputProps}
              type="number"
              fullWidth
              placeholder="1234567"
              disabled={disabled}
            />
          </Box>
        </Grid>
        <Grid item md={6}>
          <InputLabelBase className="classes.inputLabelCustom" required>
            Number of Employees
          </InputLabelBase>
          <TextField
            name="noOfEmployeesAll"
            defaultValue={values.noOfEmployeesAll}
            inputProps={inputProps}
            type="number"
            fullWidth
            placeholder="132"
            disabled={disabled}
          />
        </Grid>
        <Grid show={SHOW_YEAR_ESTABLISHED} item md={6}>
          <InputLabelBase className="classes.inputLabelCustom" required>
            Year Established
          </InputLabelBase>

          <TextField
            name="yearEstablished"
            defaultValue={values.yearEstablished}
            inputProps={{ ...inputProps, maxLength: 4 }}
            fullWidth
            placeholder="1985"
            disabled={disabled}
          />
        </Grid>
        <Grid show={SHOW_ENTITY_TYPE} item md={6}>
          <Select
            name="entityType"
            label="Entity Type"
            controllerProps={{ control: methods.control }}
            defaultValue={values.entityType}
            options={entityTypeOptions}
            fullWidth
            variant="contained"
            disabled={disabled}
            required
            InputLabelProps={{ indent: false }}
            ErrorProps={{ renderErrorPlaceholder: true }}
          />
        </Grid>
        <Grid show={SHOW_OWNERSHIP_TYPE} item md={6}>
          <Select
            name="ownershipType"
            label="Ownership Type"
            controllerProps={{ control: methods.control }}
            defaultValue={values.ownershipType}
            options={ownershipTypeOptions}
            fullWidth
            variant="contained"
            disabled={disabled}
            required
            InputLabelProps={{ indent: false }}
            ErrorProps={{ renderErrorPlaceholder: true }}
          />
        </Grid>
        <Grid show={SHOW_IS_FRANCHISE} item md={6}>
          <Select
            name="isFranchise"
            label="Subsidiary/Franchisee"
            required
            defaultValue={values.isFranchise === 'Yes' ? 'Yes' : 'No'}
            options={yesNoOptions}
            fullWidth
            variant="contained"
            disabled={disabled}
            InputLabelProps={{ indent: false }}
            ErrorProps={{ renderErrorPlaceholder: true }}
          />
        </Grid>
        <Grid show={SHOW_DOMAIN_NAME} item md={6}>
          <InputLabelBase>Primary Web Domain</InputLabelBase>
          <DomainField
            name="domainName"
            defaultValue={values.domainName}
            inputProps={inputProps}
            fullWidth
            placeholder="domain.com"
            disabled={disabled}
            ErrorProps={{ renderErrorPlaceholder: true }}
          />
        </Grid>
        <Grid show={SHOW_DOMAINS} item md={6}>
          <InputLabelBase className="classes.inputLabelCustom">
            Secondary Web Domain(s)
          </InputLabelBase>
          <DomainField
            name="domains"
            defaultValue={values.domains}
            inputProps={inputProps}
            fullWidth
            placeholder="domain.com, domain.net"
            disabled={disabled}
            ErrorProps={{ renderErrorPlaceholder: true }}
          />
        </Grid>
        <Grid show={SHOW_URL} item md={6}>
          <InputLabelBase className="classes.inputLabelCustom">
            URL
          </InputLabelBase>
          <DomainField
            name="url"
            defaultValue={values.url}
            inputProps={inputProps}
            fullWidth
            disabled={disabled}
            ErrorProps={{ renderErrorPlaceholder: true }}
          />
        </Grid>
        <Grid show={SHOW_NO_PERSONAL_RECORDS} item md={6}>
          <InputLabelBase className="classes.inputLabelCustom">
            Number of PII / PHI / PCI Records
          </InputLabelBase>
          <TextField
            name="numberOfPersonalRecords"
            defaultValue={values.numberOfPersonalRecords}
            inputProps={inputProps}
            type="number"
            fullWidth
            disabled={disabled}
          />
        </Grid>
        <Grid show={SHOW_INT_SALES} item md={6}>
          <InputLabelBase className="classes.inputLabelCustom">
            % of International Sales
          </InputLabelBase>
          <TextField
            name="percentInternationalSales"
            defaultValue={values.percentInternationalSales}
            inputProps={inputProps}
            type="number"
            fullWidth
            disabled={disabled}
          />
        </Grid>
        <Grid show={SHOW_ADDRESS} item md={6}>
          <InputLabelBase className="classes.inputLabelCustom" required>
            Address 1
          </InputLabelBase>
          <TextField
            name="address1"
            defaultValue={values.address1}
            inputProps={inputProps}
            fullWidth
            placeholder="123 Main St."
            disabled={disabled}
          />
        </Grid>
        <Grid show={SHOW_CITY} item md={6}>
          <InputLabelBase className="classes.inputLabelCustom" required>
            City
          </InputLabelBase>
          <TextField
            name="city"
            defaultValue={values.city}
            inputProps={inputProps}
            placeholder="San Marco"
            disabled={disabled}
          />
        </Grid>
        <Grid show={SHOW_STATE} item md={6}>
          <InputLabelBase className="classes.inputLabelCustom" required>
            State
          </InputLabelBase>
          <Select
            name="state"
            controllerProps={{ control: methods.control }}
            defaultValue={values.state}
            options={UsStates}
            fullWidth
            fullHeight
            variant="contained"
            disabled={disabled}
          />
        </Grid>
        <Grid show={SHOW_ZIP_CODE} item md={6}>
          <InputLabelBase className="classes.inputLabelCustom" required>
            Zip Code
          </InputLabelBase>
          <TextField
            name="zipCode"
            defaultValue={values.zipCode}
            inputProps={inputProps}
            placeholder="92024"
            disabled={disabled}
          />
        </Grid>
        <Grid show={SHOW_PHONE_NUMBER} item md={6}>
          <InputLabelBase className="classes.inputLabelCustom" required>
            Business Phone Number
          </InputLabelBase>
          <PhoneField
            name="phoneNumber"
            defaultValue={values.phoneNumber}
            inputProps={inputProps}
            fullWidth
            placeholder="(123) 456-7890"
            disabled={disabled}
          />
        </Grid>
      </Grid>
  );
};

const DomainField = (props: any) => {
  const { setValue } = useFormContext();

  function handleInputChange(originalValue: any, cleanedValue: any) {
    setValue(props.name, cleanedValue);
  }

  return <DomainFieldBase onInputChange={handleInputChange} {...props} />;
};
